import type { FetchResult } from '@apollo/client';
/**
 * Determine whether an Apollo GraphQL mutation response represents a failure.
 *
 * Apollo returns mutation payloads under a key that matches the operation name
 * (e.g. `saveExtraction`, `completeExtraction`). This helper reads the first
 * top-level field from `response.data` and inspects its `success` flag.
 *
 * Contract
 * - Input: an object shaped like `{ data: { <operationName>: { success?: boolean } } }`.
 * - Output: boolean — `true` when the mutation failed; `false` when it succeeded.
 * - Safety: If `response`/`data`/`success` is missing, it's treated as a failure (returns `true`).
 *
 * Examples
 * @example
 * // Success example (operation name: saveExtraction)
 * const res1 = { data: { saveExtraction: { success: true, id: "123" } } };
 * mutationFailed(res1); // false
 *
 * @example
 * // Failure example (explicit success: false)
 * const res2 = { data: { completeExtraction: { success: false, errors: ["Invalid state"] } } };
 * mutationFailed(res2); // true
 *
 * @example
 * // Unexpected shape (no data or no success flag) — treated as failure
 * mutationFailed({}); // true
 * mutationFailed({ data: { updateSomething: {} } }); // true
 *
 * @param response - Apollo GraphQL mutation response object.
 * @returns true if the mutation failed; false otherwise.
 */
export const mutationFailed = <T extends Record<string, { success?: boolean }>>(
  response: Pick<FetchResult<T>, 'data'> | null | undefined
) => {
  // GraphQL returns the mutation result under a key matching the operation name.
  // Read the first top-level field to support both save and complete mutations.
  const result = response?.data
    ? (Object.values(response.data)[0] as { success?: boolean })
    : null;
  return !result?.success;
};
